import enUS from "antd/es/locale/en_US";
import hyAM from "antd/es/locale/hy_AM";
import kaGE from "antd/es/locale/ka_GE";
import ukUA from "antd/es/locale/uk_UA";

/**
 * antd ships its own strings — the password reveal label, Table/Pagination/DatePicker copy.
 * Without handing `ConfigProvider` the matching locale they stay English no matter what
 * `lang.js` says. All four locales exist upstream.
 */
const ANTD_LOCALES = { en: enUS, uk: ukUA, hy: hyAM, ka: kaGE };

export const antdLocaleFor = code => ANTD_LOCALES[code] || enUS;

/**
 * The single place Ant Design's theme is configured.
 *
 * Values mirror docs/design/tokens.css — see docs/design/spec/01-foundations.md. Per the spec,
 * do NOT override colour or radius per-component; change it here so the whole app moves together.
 *
 * tokens.css stays loaded alongside this: antd's theme does not cover the status -700 text
 * values, the -bg tints, the elevation ramp, the chart series or the layout dimensions, and our
 * own components consume those as CSS custom properties.
 */
export const antdTheme = {
  token: {
    colorPrimary: "#0077A8", // --action-primary. NOT #00ABEB (2.6:1 on white, fails AA).
    colorLink: "#0077A8",
    colorError: "#BA1A1A", // --danger-500
    colorSuccess: "#1E8E3E", // --success-500
    colorWarning: "#B26A00", // --warning-500
    colorTextBase: "#171C20", // --neutral-900
    colorBorder: "#BFC8D0", // --border-default
    /* Dividers, table row rules, menu separators. antd's default is rgba(5,5,5,.06), a warm grey
       that sits visibly off the blue-grey borders the rest of the design uses. */
    colorSplit: "#DAE2E9", // --border-subtle
    colorBgLayout: "#F4F8FC", // --surface-subtle
    /* Every hoverable row in the design lands on --surface-subtle — menu items, select options,
       table rows, quiet buttons. antd's defaults are translucent neutral washes
       (rgba(23,28,32,.04) / .06), which read grey-warm against our blue-grey surfaces and made
       a dropdown row hover differently from the control that opened it. */
    controlItemBgHover: "#F4F8FC", // --surface-subtle: Menu/Dropdown/Select rows
    /* Hovering a row that is ALREADY selected is its own antd state, derived from colorPrimary —
       a saturated #8CCEDB that turned the picked option in a dropdown into a solid blue bar. The
       design marks selection with colour, weight and a tick; the hover stays just the hover. */
    controlItemBgActiveHover: "#F4F8FC", // --surface-subtle
    colorBgTextHover: "#F4F8FC", // --surface-subtle: type="text" buttons
    colorBgTextActive: "#EAF0F5", // --surface-sunken: one step further on press
    borderRadius: 8, // --radius-md
    // antd derives borderRadiusLG as borderRadius + 2, which puts large controls on 10px.
    // The design has one control radius, so pin it back to --radius-md.
    borderRadiusLG: 8,
    /* --radius-sm, the spec's radius for "tags, chips, small inputs". antd derives 4px, which
       reads square next to the 8px fields the chips sit inside. */
    borderRadiusSM: 6,
    /* Denser than antd's own 32 default was the redesign's first pass at 40, but 40 read
       magnified across the app; 36 is the density this dashboard runs at. Still ≥ WCAG 2.5.8's
       24px target-size minimum. Mirror in docs/design/tokens.css + spec/01-foundations.md. */
    controlHeight: 36, // --control-h
    controlHeightLG: 44, // --control-h-lg — overrides antd's 40
    controlHeightSM: 32, // --control-h-sm
    fontFamily: "'Noto Sans','Noto Sans Armenian','Noto Sans Georgian',system-ui,-apple-system,'Segoe UI',Roboto,sans-serif",
    fontSize: 14,
  },
  components: {
    /* Input carries NO paddingInline override. It used to be 16 (--space-4), but the mockups put
       every control on the same 11px inset — `.ctl { padding: 0 11px }`, `.combo__ctl` and
       `.rp__ctl` likewise — and Select/DatePicker have no matching token, so a text field sat 5px
       further in than the select beside it in the same filter row. antd's default is 11. */
    /* A default antd button hovers by painting its border AND label primary-5 (#1D8CB5). The
       design's is a grey wash with the border and label unchanged (`.btn:hover` in the mockups),
       and blue-on-hover also reads as "this is the primary action", which a default button is
       not. Fixed here rather than per-component: the app-bar language trigger is where it was
       first noticed, but every default Button in the app had it. */
    /* The sidebar is an antd DARK Menu, whose hover background is its own token and defaults to
       transparent — so `.shell-nav.ant-menu .ant-menu-item:hover` (four classes) lost to
       `.ant-menu-dark:not(.ant-menu-horizontal) .ant-menu-item:not(.ant-menu-item-selected):hover`
       (six) and nav rows answered the pointer with a text-colour change only. The design's is
       `rgba(255,255,255,.07)`. */
    Menu: { darkItemHoverBg: "rgba(255,255,255,0.07)" },
    /* The range panel is 656px wide in the mockups and 875px in stock antd — a 48×44 cell grid
       against the design's 34×30. See `src/component/PeriodPicker.js`. */
    /* The trailing caret and the clear button. antd's `fontSizeIcon` is 12 and drives the glyph's
       width, height AND its centring margin — so it cannot be fixed with CSS `font-size` alone.
       The mockups draw both at 18, the same size as the leading glyph on the same row
       (`.combo__caret .ms`, `.rp__clear .ms`, `.combo__clear .ms`); at 12 they read as a second,
       smaller control kit. Scoped per component rather than set globally, which would also grow
       every Modal close, Tag close and table filter icon in the app. */
    DatePicker: {
      fontSizeIcon: 18,
      cellHeight: 30,
      cellWidth: 32, // 7 × 32 + 16px body inset = 240, against the design's 236px calendar
      textHeight: 34, // the month header row; antd uses controlHeightLG (48) and reads cavernous
      presetsWidth: 158,
      presetsMaxWidth: 158,
      cellActiveWithRangeBg: "#E6F7FE", // --brand-50, the in-range wash
      cellHoverBg: "#F4F8FC", // --surface-subtle, same hover as every other row
    },
    /* The entity dropdown — `src/component/EntitySelect.js`, the mockups' `.combo`. A selected row
       there is marked by colour, weight and a tick, NOT by a filled bar, so the hover wash stays
       the only background in the list and "hovered" never reads as "chosen". */
    Select: {
      fontSizeIcon: 18, // caret + clear — see the DatePicker note above
      multipleItemBg: "#EAF0F5", // --surface-sunken, the chip fill
      multipleItemBorderColor: "transparent",
      optionSelectedBg: "transparent",
      optionSelectedColor: "#0077A8", // --action-primary
      optionSelectedFontWeight: 600,
      optionActiveBg: "#F4F8FC", // --surface-subtle, the same hover as every other row
      optionHeight: 34,
      optionPadding: "8px 12px",
    },
    Button: {
      defaultHoverBg: "#F4F8FC", // --surface-subtle
      defaultHoverColor: "#171C20", // --neutral-900 / --text-primary
      defaultHoverBorderColor: "#BFC8D0", // --border-default — i.e. unchanged from rest
      defaultActiveBg: "#EAF0F5", // --surface-sunken, one step further on press
      defaultActiveColor: "#171C20",
      defaultActiveBorderColor: "#BFC8D0",
      /* The mockups' `.btn` is a 500-weight label; antd's is 400, which read visibly lighter than
         the design next to the same 14px body text. `.btn--primary` is 600 — one token cannot
         express both, so the primary's weight is the one CSS rule in `antd.scss`. */
      fontWeight: 500,
      /* `.btn` carries no shadow and `.btn--primary` carries --shadow-brand. antd's defaults are
         a 1px `0 2px 0` ledge under every button, which reads as a Bootstrap-era press effect. */
      defaultShadow: "none",
      dangerShadow: "none",
      primaryShadow: "0 4px 12px rgba(0, 119, 168, 0.28)", // --shadow-brand
      // `.btn` is `0 14px`, `.btn--sm` is `0 11px`; antd ships 15 and 7.
      paddingInline: 14,
      paddingInlineSM: 11,
    },
  },
};

export default antdTheme;
